import { useMemo, useRef, useState } from "react"
import { CalendarBlank, PencilSimple, TShirt } from "@phosphor-icons/react"

import {
  Badge,
  Button,
  DateRangePicker,
  EmptyState,
  ExpandableCard,
  ExpandableCardContent,
  ExpandableCardHeader,
  InlineAlert,
  Input,
  PermissionTooltip,
  RadioGroup,
  RadioGroupItem,
} from "~/components/design-system/components"
import ExpandableSearchField from "~/components/planning/ExpandableSearchField"
import { Tooltip } from "~/components/reusable/Tooltip"
import { usePermission } from "~/hooks/usePermission"
import { useVariants } from "~/hooks/useVariants"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { pluralize } from "~/utils/formatting"
import {
  DateFormatType,
  differenceInCalendarDaysUTC,
  formatDate,
  toUTCDateString,
} from "~/utils/datetime"
import { PACKAGE_NAME_MAX, packageName } from "~/utils/allocationPackageOps"
import { matchesLineSearch, VARIANT_SEARCH_OPTIONS } from "~/utils/lineSearch"
import { showErrorToast } from "~/utils/toaster"
import {
  formatDayShift,
  formatPackageDate,
  focusAndSelect,
  OVERLAP_ADVICE,
  OVERLAP_TITLE,
  overlapSummary,
  overlapTooltip,
  PACKAGE_STATUS_META,
  PACKAGE_TYPE_BADGE,
  PACKAGE_TYPE_LABEL,
} from "./packageDisplay"
import { CardTitle, PACKAGE_CARD_KEY } from "./PackageCardTitle"
import { DeletionImpact } from "./PackageDeletionImpact"
import { FollowOrOwn } from "./FollowOrOwn"
import { folderPath, packageFolder } from "./packageFolders"
import { PackageActionsMenu } from "./PackageMenus"
import { pageDisabledReason } from "./packagePageGating"
import { usePackageNavigator, usePageRequest } from "./packageNavigator"
import { PackageStoresCard, useStoresCardExpanded } from "./PackageStoresCard"
import { noteRunAfterCurrent, usePackageDraft } from "./setup/setupDrafts"
import { type PackageFolder, type RangePackage } from "./types"
import { useAllocationMutations } from "./useAllocationData"
import { useLineSearch } from "./useLineSearch"
import { showUndoToast } from "./undoToast"

const PACKAGE_RANGE_SEARCH_ID = "allocation-package-range"

type SavePackagePage = ReturnType<typeof useAllocationMutations>["savePackagePage"]

function PackageName({
  pkg,
  nameRef,
  disabledReason,
  save,
}: {
  pkg: RangePackage
  nameRef: React.RefObject<HTMLInputElement | null>
  disabledReason: string | undefined
  save: SavePackagePage
}) {
  const [draft, setDraft] = useState(pkg.name)

  const commit = () => {
    if (draft.trim() === pkg.name.trim()) return setDraft(pkg.name)
    const parsed = packageName.safeParse(draft)
    if (!parsed.success) {
      showErrorToast(new Error(parsed.error.issues[0]?.message))
      return setDraft(pkg.name)
    }
    const name = parsed.data
    save.mutate(
      { op: { kind: "renamePackage", packageId: pkg.id, name } },
      {
        onError: (error) => {
          setDraft(pkg.name)
          showErrorToast(error)
        },
      },
    )
  }

  return (
    <Input
      ref={nameRef}
      variant="transparent"
      aria-label="Package name"
      className="header-h2! field-sizing-content max-w-full rounded-md px-1.5"
      containerClassName="min-w-0"
      value={draft}
      maxLength={PACKAGE_NAME_MAX}
      requires="edit"
      disabled={disabledReason !== undefined}
      allowedTitle={disabledReason}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur()
        if (event.key === "Escape") {
          const input = event.currentTarget
          setDraft(pkg.name)
          requestAnimationFrame(() => input.blur())
        }
      }}
    />
  )
}

function PackageHeader({
  pkg,
  disabledReason,
  save,
  nameRef,
  onDelete,
}: {
  pkg: RangePackage
  disabledReason: string | undefined
  save: SavePackagePage
  nameRef: React.RefObject<HTMLInputElement | null>
  onDelete: () => void
}) {
  const nav = usePackageNavigator()
  const status = PACKAGE_STATUS_META[pkg.status]
  const deleting = usePackageDraft().isDeleting(pkg.id)
  const path = folderPath(nav.folders, pkg.folderId)
  return (
    <div className="flex flex-col gap-1">
      {path.length > 0 && (
        <nav aria-label="Folder" className="flex items-center gap-1">
          {path.map((folder) => (
            <Button
              key={folder.id}
              variant="transparent"
              size="sm"
              onClick={() => nav.select({ kind: "folder", id: folder.id })}
            >
              {folder.name} ›
            </Button>
          ))}
        </nav>
      )}
      <div className="flex items-center gap-3">
        <PackageName
          key={pkg.name}
          pkg={pkg}
          nameRef={nameRef}
          disabledReason={disabledReason}
          save={save}
        />
        <Badge variant={PACKAGE_TYPE_BADGE[pkg.type]}>{PACKAGE_TYPE_LABEL[pkg.type]}</Badge>
        <Badge variant={status.variant}>{status.label}</Badge>
        {deleting && <span className="label-sm text-fg-muted">· deleting on save</span>}
        <span className="label-sm text-fg-muted">
          {pluralize(pkg.productIds.length, "variant")} ·{" "}
          {pluralize(pkg.skuIds?.length ?? 0, "SKU")}
        </span>
        <div className="flex-1" />
        <PackageActionsMenu
          pkg={pkg}
          place="header"
          onRename={() => focusAndSelect(nameRef)}
          onDelete={onDelete}
        />
      </div>
    </div>
  )
}

type DateBound = "start" | "cutoff"
const OWN_DATES_CHOICES = ["shift", "keep"] as const
type OwnDatesChoice = (typeof OWN_DATES_CHOICES)[number]
const isOwnDatesChoice = (value: string): value is OwnDatesChoice =>
  (OWN_DATES_CHOICES as readonly string[]).includes(value)
type Window = { startDate: string; cutoffDate: string }

type PendingWindow = {
  window: Window
  bounds: DateBound[]
  step: number
  choices: Record<DateBound, OwnDatesChoice>
}

const toDate = (day: string) => new Date(`${day}T00:00:00Z`)

const arrowWindowLabel = (window: Window) =>
  `${formatPackageDate(window.startDate)} → ${formatPackageDate(window.cutoffDate)}`

function dateFieldLabel(date: Date) {
  return formatDate(date, DateFormatType.FullDateShort)
}

function ownDatesHint(pkg: RangePackage) {
  if (pkg.ownDatesVariantCount === 0) return "Every variant in this package follows these dates."
  const own = pluralize(pkg.ownDatesVariantCount, "variant")
  return `Changing these dates moves every variant that follows the package. ${own} with their own dates can be shifted along or kept.`
}

function OwnDatesConfirmation({
  pkg,
  current,
  pending,
  saving,
  onChoose,
  onCancel,
  onApply,
}: {
  pkg: RangePackage
  current: Window
  pending: PendingWindow
  saving: boolean
  onChoose: (choice: OwnDatesChoice) => void
  onCancel: () => void
  onApply: () => void
}) {
  const bound = pending.bounds[pending.step]!
  const isStart = bound === "start"
  const from = isStart ? current.startDate : current.cutoffDate
  const to = isStart ? pending.window.startDate : pending.window.cutoffDate
  const days = differenceInCalendarDaysUTC(toDate(to), toDate(from))
  const followers = pkg.productIds.length - pkg.ownDatesVariantCount
  const own = pkg.ownDatesVariantCount
  const boundLabel = isStart ? "start" : "replenishment cut-off"
  const isLast = pending.step === pending.bounds.length - 1

  return (
    <InlineAlert
      tone="warning"
      size="block"
      title={`Move the package ${boundLabel} to ${formatPackageDate(to)}?`}
      description={
        <div className="flex flex-col gap-2">
          <span>
            {pluralize(followers, "variant")} {followers === 1 ? "follows" : "follow"} the package
            and {followers === 1 ? "moves" : "move"} with it. {pluralize(own, "variant")}{" "}
            {own === 1 ? "has" : "have"} dates set by hand:
          </span>
          <RadioGroup
            value={pending.choices[bound]}
            onValueChange={(value) => {
              if (isOwnDatesChoice(value)) onChoose(value)
            }}
          >
            <label className="flex items-start gap-2">
              <RadioGroupItem value="shift" className="mt-0.5" />
              <span className="flex flex-col">
                <span className="label-base text-fg-base">
                  Shift them too ({formatDayShift(days)})
                </span>
                <span className="paragraph-sm text-fg-muted">
                  Their own {isStart ? "start" : "cut-off"} dates move by the same amount.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <RadioGroupItem value="keep" className="mt-0.5" />
              <span className="flex flex-col">
                <span className="label-base text-fg-base">Keep their own dates</span>
                <span className="paragraph-sm text-fg-muted">
                  Only variants that follow the package move.
                </span>
              </span>
            </label>
          </RadioGroup>
        </div>
      }
      secondaryAction={{ label: "Cancel", onClick: onCancel }}
      action={{
        label: isLast ? "Apply" : "Next",
        onClick: onApply,
        requires: "edit",
        disabled: saving,
      }}
    />
  )
}

function DatesSource({
  pkg,
  folders,
  current,
  disabledReason,
  save,
}: {
  pkg: RangePackage
  folders: PackageFolder[]
  current: Window | null
  disabledReason: string | undefined
  save: SavePackagePage
}) {
  const following = pkg.datesFolderId !== null
  const folder = packageFolder(folders, pkg.folderId)
  if (pkg.type === "noos" || !folder || (!following && pkg.startDate === null)) return null
  const setWindow = (window: Window | null) =>
    save.mutate(
      {
        op: {
          kind: "setPackageWindow",
          packageId: pkg.id,
          window,
          shiftOwnStart: false,
          shiftOwnCutoff: false,
        },
      },
      { onSuccess: (result) => noteRunAfterCurrent(result.run), onError: showErrorToast },
    )
  return (
    <FollowOrOwn
      of="dates"
      folderName={folder.name}
      following={following}
      saving={save.isPending}
      disabledReason={
        disabledReason ??
        (following && current === null ? `${folder.name} has no dates yet` : undefined)
      }
      onChange={(follow) => setWindow(follow ? null : current)}
    />
  )
}

function datesMeta(pkg: RangePackage, current: Window | null) {
  if (current) return arrowWindowLabel(current)
  return pkg.type === "noos" ? "Always open" : "No dates"
}

function PackageDatesCard({
  pkg,
  folders,
  disabledReason,
  save,
  onSelectFolder,
}: {
  pkg: RangePackage
  folders: PackageFolder[]
  disabledReason: string | undefined
  save: SavePackagePage
  onSelectFolder: (folderId: string) => void
}) {
  const [pending, setPending] = useState<PendingWindow | null>(null)
  const { allowed } = usePermission("edit")
  const following = pkg.datesFolderId !== null
  // In a folder without dates anywhere above it: point at the folder instead.
  const noDatesFolder =
    pkg.type !== "noos" && pkg.startDate === null
      ? folders.find((folder) => folder.id === pkg.folderId)
      : undefined
  const current =
    pkg.startDate && pkg.cutoffDate
      ? { startDate: pkg.startDate, cutoffDate: pkg.cutoffDate }
      : null

  const commit = (window: Window, shiftOwnStart: boolean, shiftOwnCutoff: boolean) =>
    save.mutate(
      {
        op: { kind: "setPackageWindow", packageId: pkg.id, window, shiftOwnStart, shiftOwnCutoff },
      },
      { onSuccess: () => setPending(null), onError: showErrorToast },
    )

  // Editing dates it follows gives it its own; Undo follows the folder again.
  const nav = usePackageNavigator()
  const windowOp = (window: Window | null) =>
    ({
      kind: "setPackageWindow",
      packageId: pkg.id,
      window,
      shiftOwnStart: false,
      shiftOwnCutoff: false,
    }) as const
  const takeOwn = (window: Window) =>
    nav.savePage(windowOp(window), () =>
      showUndoToast(`${pkg.name} now uses its own dates`, () => nav.savePage(windowOp(null))),
    )

  const onPick = (from: Date | undefined, to: Date | undefined) => {
    if (!from || !to || !current) return
    const window = { startDate: toUTCDateString(from), cutoffDate: toUTCDateString(to) }
    const bounds: DateBound[] = []
    if (window.startDate !== current.startDate) bounds.push("start")
    if (window.cutoffDate !== current.cutoffDate) bounds.push("cutoff")
    if (bounds.length === 0) return
    if (following) return takeOwn(window)
    if (pkg.ownDatesVariantCount === 0) return commit(window, false, false)
    setPending({ window, bounds, step: 0, choices: { start: "shift", cutoff: "shift" } })
  }

  const advance = () => {
    if (!pending) return
    if (pending.step < pending.bounds.length - 1) {
      return setPending({ ...pending, step: pending.step + 1 })
    }
    const shifts = (bound: DateBound) =>
      pending.bounds.includes(bound) && pending.choices[bound] === "shift"
    commit(pending.window, shifts("start"), shifts("cutoff"))
  }

  const weeks = current
    ? Math.max(
        1,
        Math.round(
          differenceInCalendarDaysUTC(toDate(current.cutoffDate), toDate(current.startDate)) / 7,
        ),
      )
    : 0

  // First dates for an undated package: they become its own.
  const firstDatesPicker = (
    <PermissionTooltip requires="edit" allowedTitle={disabledReason}>
      <div className="mx-auto w-[280px]">
        <DateRangePicker
          startDate={undefined}
          endDate={undefined}
          onApply={({ from, to }) => {
            if (from && to) {
              commit(
                { startDate: toUTCDateString(from), cutoffDate: toUTCDateString(to) },
                false,
                false,
              )
            }
          }}
          formatDate={dateFieldLabel}
          validateRange={(from, to) =>
            from && to ? null : "Pick a start date and a replenishment cut-off."
          }
          align="start"
          disabled={!allowed || disabledReason !== undefined || save.isPending}
        />
      </div>
    </PermissionTooltip>
  )

  return (
    <ExpandableCard className="shrink-0" persistKey={`${PACKAGE_CARD_KEY}.dates`} defaultExpanded>
      <ExpandableCardHeader>
        <CardTitle meta={datesMeta(pkg, current)}>Dates</CardTitle>
      </ExpandableCardHeader>
      <ExpandableCardContent className="flex flex-col gap-3">
        <DatesSource
          pkg={pkg}
          folders={folders}
          current={current}
          disabledReason={disabledReason}
          save={save}
        />
        {pkg.type === "noos" ? (
          <p className="paragraph-sm text-fg-muted">
            Always open. A NOOS package has no start date or replenishment cut-off; it stays open
            until it is archived.
          </p>
        ) : current ? (
          <>
            <div className="flex items-center gap-3">
              <PermissionTooltip requires="edit" allowedTitle={disabledReason}>
                <div className="w-[280px]">
                  <DateRangePicker
                    startDate={toDate((pending?.window ?? current).startDate)}
                    endDate={toDate((pending?.window ?? current).cutoffDate)}
                    onApply={({ from, to }) => onPick(from, to)}
                    formatDate={dateFieldLabel}
                    validateRange={(from, to) =>
                      from && to ? null : "Pick a start date and a replenishment cut-off."
                    }
                    align="start"
                    disabled={!allowed || disabledReason !== undefined || pending !== null}
                  />
                </div>
              </PermissionTooltip>
              <span className="label-sm text-fg-muted">
                {pluralize(weeks, "week")} · start date to replenishment cut-off
              </span>
            </div>
            {following && (
              <p className="paragraph-sm text-fg-muted">
                {"Follows the folder's dates. Changing them gives this package its own dates."}
              </p>
            )}
            {!following && pending ? (
              <OwnDatesConfirmation
                pkg={pkg}
                current={current}
                pending={pending}
                saving={save.isPending}
                onChoose={(choice) =>
                  setPending({
                    ...pending,
                    choices: { ...pending.choices, [pending.bounds[pending.step]!]: choice },
                  })
                }
                onCancel={() => setPending(null)}
                onApply={advance}
              />
            ) : (
              !following && <p className="paragraph-sm text-fg-muted">{ownDatesHint(pkg)}</p>
            )}
          </>
        ) : noDatesFolder ? (
          <>
            <EmptyState
              icon={CalendarBlank}
              title={`${noDatesFolder.name} has no dates yet`}
              description="This package follows its folder's dates, so nothing replenishes until the folder has dates. Or give this package dates of its own."
              action={{
                label: `Set dates on ${noDatesFolder.name}`,
                onClick: () => onSelectFolder(noDatesFolder.id),
              }}
            />
            {firstDatesPicker}
          </>
        ) : (
          <>
            <EmptyState
              icon={CalendarBlank}
              title="When does this package sell?"
              description="Nothing replenishes until the package has dates. Seasonal packages need a start date and a replenishment cut-off."
            />
            {firstDatesPicker}
          </>
        )}
      </ExpandableCardContent>
    </ExpandableCard>
  )
}

function getProductsForPackage(pkg: RangePackage, variants: IVariantSummary[]) {
  const ids = new Set(pkg.productIds)
  return variants
    .filter((v) => ids.has(v.variantId))
    .map((v) => ({
      id: v.variantId,
      name: v.variantDisplayName,
      category: v.category ?? "Other",
      variant: v,
    }))
}

function PackageVariantsCard({
  pkg,
  disabledReason,
  onEditRange,
}: {
  pkg: RangePackage
  disabledReason: string | undefined
  onEditRange: () => void
}) {
  const variants = useVariants()
  const products = useMemo(() => getProductsForPackage(pkg, variants), [pkg, variants])
  const { search, setSearch, searchBy, setSearchBy } = useLineSearch(
    PACKAGE_RANGE_SEARCH_ID,
    VARIANT_SEARCH_OPTIONS,
  )

  const filtered = useMemo(
    () => products.filter((p) => matchesLineSearch(p, search, searchBy, (row) => row.variant)),
    [products, search, searchBy],
  )

  const grouped = useMemo(() => Map.groupBy(filtered, (p) => p.category), [filtered])
  const editRange = {
    requires: "edit" as const,
    disabled: disabledReason !== undefined,
    allowedTitle: disabledReason,
  }

  return (
    <ExpandableCard
      className="shrink-0"
      persistKey={`${PACKAGE_CARD_KEY}.variants`}
      defaultExpanded
    >
      <ExpandableCardHeader>
        <CardTitle meta={pluralize(pkg.productIds.length, "variant")}>Variants</CardTitle>
        <div className="flex-1" />
        {pkg.productIds.length > 0 && (
          <>
            <ExpandableSearchField
              initialSearchText={search}
              onSearchTextChange={setSearch}
              placeholder="Search..."
              debounceTime={200}
              searchBy={searchBy}
              onSearchByChange={setSearchBy}
              searchByOptions={VARIANT_SEARCH_OPTIONS}
            />
            <Button
              variant="transparent"
              size="xs"
              className="gap-1"
              {...editRange}
              onClick={onEditRange}
            >
              <PencilSimple size={12} />
              Edit range
            </Button>
          </>
        )}
      </ExpandableCardHeader>
      <ExpandableCardContent>
        {pkg.productIds.length === 0 ? (
          <EmptyState
            icon={TShirt}
            title="Your variants will show here"
            description="This is where you choose what the package sells. Pick variants from the product master."
            action={{ label: "Add variants", onClick: onEditRange, ...editRange }}
          />
        ) : (
          <div className="space-y-4">
            {[...grouped.entries()].map(([category, items]) => (
              <div key={category}>
                <div className="mb-1.5 flex items-baseline gap-2">
                  <h4 className="text-xs font-semibold text-fg-base">{category}</h4>
                  <span className="text-[11px] text-fg-subtle">
                    {pluralize(items.length, "variant")}
                  </span>
                </div>
                <div className="space-y-1">
                  {items.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center gap-2 rounded-md border border-border-base px-3 py-1.5"
                    >
                      <span className="flex-1 text-sm text-fg-base">{p.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {grouped.size === 0 && (
              <p className="text-sm text-fg-subtle">
                {products.length === 0
                  ? "Variant details are not available yet."
                  : "No variants match your search."}
              </p>
            )}
          </div>
        )}
      </ExpandableCardContent>
    </ExpandableCard>
  )
}

function OverlapBar({
  overlap,
  disabledReason,
  onOpenStores,
  onEditRange,
}: {
  overlap: NonNullable<RangePackage["overlap"]>
  disabledReason: string | undefined
  onOpenStores: () => void
  onEditRange: () => void
}) {
  const text = `${overlapSummary(overlap)} ${OVERLAP_ADVICE}`
  return (
    <InlineAlert
      tone="warning"
      className="shrink-0 flex-nowrap"
      description={
        <Tooltip title={overlapTooltip(overlap, true)}>
          <span className="block truncate">
            <span className="label-sm text-fg-base">{OVERLAP_TITLE}</span> {text}
          </span>
        </Tooltip>
      }
      secondaryAction={{
        label: "Edit variants",
        onClick: onEditRange,
        requires: "edit",
        disabled: disabledReason !== undefined,
        allowedTitle: disabledReason,
      }}
      action={{ label: "Open stores", onClick: onOpenStores }}
    />
  )
}

export function PackagePage({ pkg }: { pkg: RangePackage }) {
  const nav = usePackageNavigator()
  const { savePackagePage } = useAllocationMutations()
  const draft = usePackageDraft()
  const nameRef = useRef<HTMLInputElement>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const deleting = draft.isDeleting(pkg.id)
  const disabledReason = pageDisabledReason(pkg, nav.folders, deleting)
  usePageRequest(
    { kind: "package", id: pkg.id },
    {
      rename: () => focusAndSelect(nameRef),
      delete: () => setConfirmDelete(true),
    },
  )
  const storesRef = useRef<HTMLDivElement>(null)
  const [, setStoresExpanded] = useStoresCardExpanded(pkg)
  const openStores = () => {
    setStoresExpanded(true)
    requestAnimationFrame(() =>
      storesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
    )
  }
  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-6">
      <PackageHeader
        pkg={pkg}
        disabledReason={disabledReason}
        save={savePackagePage}
        nameRef={nameRef}
        onDelete={() => setConfirmDelete(true)}
      />
      {confirmDelete && !deleting && (
        <DeletionImpact
          target={{ packageId: pkg.id }}
          label={pkg.name}
          onCancel={() => setConfirmDelete(false)}
          onMarked={() => setConfirmDelete(false)}
        />
      )}
      {pkg.overlap && (
        <OverlapBar
          overlap={pkg.overlap}
          disabledReason={disabledReason}
          onOpenStores={openStores}
          onEditRange={() => nav.editRange(pkg)}
        />
      )}
      <PackageDatesCard
        pkg={pkg}
        folders={nav.folders}
        disabledReason={disabledReason}
        save={savePackagePage}
        onSelectFolder={(folderId) => nav.select({ kind: "folder", id: folderId })}
      />
      <PackageVariantsCard
        pkg={pkg}
        disabledReason={disabledReason}
        onEditRange={() => nav.editRange(pkg)}
      />
      <div ref={storesRef} className="shrink-0 scroll-mt-6">
        <PackageStoresCard pkg={pkg} folders={nav.folders} disabledReason={disabledReason} />
      </div>
    </div>
  )
}
